import { useEffect, useRef, useState } from "react";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import Select from "react-select";
import { Button, Col, Container, Form, Row } from "react-bootstrap";
import {
    FaCheck, FaCheckCircle,
    FaClipboardCheck,
    FaFilePdf, FaPaperPlane,
    FaPencilAlt,
    FaSave,
    FaStepBackward,
    FaTimes,
    FaTrash
} from "react-icons/fa";
import printJS from "print-js";

//Components
import { HeaderImage } from "../../shared/header_image/HeaderImage";
import { UserInformation } from "../../shared/user_information/UserInformation";
import { Loading } from "../../shared/loading/Loading";
import { DeliveryReport } from "./delivery-report/DeliveryReport";
import { ApprovedDeniedModal } from "../../shared/Modals/ApprovedDeniedModal";
import { FEModal } from "../../shared/Modals/FEModal";
import { ConfirmationModal } from "../../shared/Modals/ConfirmationModal";
import { SmartTable } from "../../../../shared/ui/smart-table";

//Hooks
import { useDeliveriesData } from "./hooks/useDeliveriesData";
import { useUserInformation } from "./hooks/useUserInformation";
import { useSuppliers } from "./hooks/useSuppliers";

//Util
import AlertComponent from "../../../../helpers/alert/AlertComponent";
import { escapeHtml } from "../../../../shared/lib/escapeHtml";

//Img
import imgDCSIPeople from "../../../../assets/image/addProducts/imgDSCIPeople.png";
import imgFrame2 from "../../../../assets/image/icons/deliveries-img.png";
import imgAdd from "../../../../assets/image/payments/imgPay.png";

//Services
import { deliveriesServices } from "../../../../helpers/services/DeliveriesServices";
import { filesServices } from "../../../../helpers/services/FilesServices";

//Enum
import {
    ResponseStatusEnum,
    RolesEnum,
    UploadFileEnum
} from "../../../../helpers/GlobalEnum";

//Opciones para los productos a entregar
const deliveryStatus = [
    { id: 0, label: "NO ENTREGADO" },
    { id: 1, label: "ENTREGADO" },
    //{ id: 2, label: "PENDIENTE POR ENTREGAR" },
    { id: 3, label: "ENTREGA PARCIAL" }
];

//
const IMAGE_SLOTS = new Set([UploadFileEnum.EVIDENCE1, UploadFileEnum.EVIDENCE2]);

const isValidImage = (file) => {
    const okMime = file.type?.startsWith('image/');
    const okExt  = /\.(jpe?g|png)$/i.test(file.name || '');
    return okMime || okExt;
};

const isValidPdf = (file) => {
    const okMime = file.type === 'application/pdf';
    const okExt  = /\.pdf$/i.test(file.name || '');
    return okMime || okExt;
};

// ============================
//   HELPERS / CONSTANTS
// ============================

/**
 * Devuelve true si la fila tiene número de factura electrónica (FE).
 * @param {Object} row - Fila del DataGrid.
 */
const hasFeNumber = (row) => row?.fe_number;
const isLeaderTechnicalRole = (roleId) =>
    roleId === RolesEnum.LIDER_TECNICO_AGRO ||
    roleId === RolesEnum.LIDER_TECNICO_NO_AGRO;
/** Roles que pueden editar entregas. */
const canEditRoles = [
    RolesEnum.TERRITORIAL_LINKS,
    RolesEnum.TECHNICAL,
    RolesEnum.LIDER_TECNICO_AGRO,
    RolesEnum.LIDER_TECNICO_NO_AGRO,
    RolesEnum.SUPPLIER,
    RolesEnum.ADMIN
];

/** Roles que pueden eliminar entregas. */
const canDeleteRoles = [
    RolesEnum.TERRITORIAL_LINKS,
    RolesEnum.TECHNICAL,
    RolesEnum.LIDER_TECNICO_AGRO,
    RolesEnum.LIDER_TECNICO_NO_AGRO,
    RolesEnum.SUPPLIER,
    RolesEnum.ADMIN
];

/**
 * Devuelve true si la fila tiene consolidado PDF cargado.
 * @param {Object} row - Fila del DataGrid.
 */
const hasPdfConsolidado = (row) => Boolean(row?.evidencePdf?.consolidatedFileUrl);

/**
 * Devuelve la URL del archivo FE (factura electrónica) de la fila.
 * @param {Object} row - Fila del DataGrid.
 */
const getFePdfUrl = (row) => row?.evidencePdf?.feFileUrl ?? null;

/**
 * Devuelve la URL del archivo Imagen1 y Imagen2 de la fila.
 * @param {Object} row - Fila del DataGrid.
 */
const getImagen1Url = (row) => row?.evidenceImg?.imgEvidence1Url ?? null;
const getImagen2Url = (row) => row?.evidenceImg?.imgEvidence2Url ?? null;

/**
 * Devuelve la URL del archivo Imagen1 y Imagen2 (factura electrónica) de la fila.
 * @param {Object} row - Fila del DataGrid.
 */
const hasPdfOK = (row) => Boolean(row?.evidencePdf?.consolidatedFileUrl && row?.evidencePdf?.feFileUrl && row?.evidenceImg?.imgEvidence1Url && row?.evidenceImg?.imgEvidence2Url);

export const Deliveries = () => {

    const { userAuth } = useOutletContext();
    const params = useParams();
    const navigate = useNavigate();
    const deliveryReportRef = useRef();

    const [selectedSupplier, setSelectedSupplier] = useState(null);
    const [showDeliveryForm, setShowDeliveryForm] = useState(false);
    const [deliveryProducts, setDeliveryProducts] = useState([]);
    const [deliveryInformation, setDeliveryInformation] = useState({});
    const [isReadyToPrintDeliveryInformation, setIsReadyToPrintDeliveryInformation] = useState(false);
    const [loading, setLoading] = useState(false);

    // FE modal state
    const [showFeModal, setShowFeModal] = useState(false);
    const [feDeliveryId, setFeDeliveryId] = useState(null);
    const [feLoading, setFeLoading] = useState(false);

    const [selectedDeliveryId, setSelectedDeliveryId] = useState(null);
    const [deliveriesPage, setDeliveriesPage] = useState(1);
    const [deliveriesPageSize, setDeliveriesPageSize] = useState(10);
    const [productsPage, setProductsPage] = useState(1);
    const [productsPageSize, setProductsPageSize] = useState(5);

    //Confirmación proveedor
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);

    //Aprobacion
    const [openModal, setOpenModal] = useState(false);
    const [comment, setComment] = useState('');
    const [action, setAction] = useState('approve');

    // Datos de entregas vía SWR: carga progresiva + revalidación selectiva por fila.
    const {
        rows: listDeliveriesToUser,
        isLoading: deliveriesLoading,
        refreshRow,
        revalidate: revalidateDeliveries,
    } = useDeliveriesData(params.id);

    // Información del titular (CUB) y proveedores del selector, cacheados vía SWR.
    const { userData, consolidated } = useUserInformation(params.id);
    const { suppliers } = useSuppliers(params.id, userAuth?.rol_id);

    /**
     * Determina si un botón debe estar deshabilitado según:
     * - Estado de aprobación (territorial o técnica).
     * - Rol del usuario autenticado.
     */
    const isButtonDisabled = (row, rolId = userAuth?.rol_id) => {
        if(row.actions.aprobadoProveedor === true && rolId === RolesEnum.SUPPLIER) {
            return true;
        }

        if(row.actions.aprobadoProveedor === true && row.actions.approvedTechnical === true && (rolId === RolesEnum.TERRITORIAL_LINKS || rolId === RolesEnum.TECHNICAL || isLeaderTechnicalRole(rolId))) {
            return true;
        }

        if((row.actions.aprobadoProveedor === true || row.actions.aprobadoProveedor === null) && (rolId === RolesEnum.TERRITORIAL_LINKS || rolId === RolesEnum.TECHNICAL || isLeaderTechnicalRole(rolId))) {
            return false;
        }

        if(rolId !== RolesEnum.TERRITORIAL_LINKS && rolId !== RolesEnum.SUPPLIER && rolId !== RolesEnum.TECHNICAL && !isLeaderTechnicalRole(rolId)) {
            return true;
        }

        if(rolId === RolesEnum.ADMIN) {
            return false;
        }
    };

    /**  Determina si el botón técnico debe estar deshabilitado según aprobación. */
    const isButtonDisabledTecnical = (row) => {
        const { aprobadoProveedor, approvedTechnical } = row.actions;

        if(approvedTechnical === true && aprobadoProveedor === true) {
            return true;
        }
        return false;
    };

    /** Renderiza el ícono de envío de entrega. */
    const renderSendSupplierIcon = (status) => {
        if (status === 1 || status === true) {
            return (
                <span
                    title="Entrega enviada"
                    style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "6px",
                        color: "#40a581",
                        fontWeight: 500,
                    }}
                >
                    <FaCheckCircle />
                    Entrega Enviada
                </span>
            )
        }
        return (
                <span
                    title="Enviar entrega"
                    style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "6px",
                        color: "#ffffff",
                        fontWeight: 500,
                    }}
                >
                    <FaPaperPlane />
                    Enviar Entrega
                </span>
        );
    }

    /** Renderiza el ícono de aprobación (aprobado, denegado o pendiente). */
    const renderApprovalIcon = (status) => {
        if (status === 1 || status === true) {
            return (
                    <span
                        title="Enviar entrega"
                        style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "6px",
                            color: "#28A745",
                            fontWeight: 500,
                        }}
                    >
                        <FaCheck />
                        Enviada a supervisión
                </span>
            );

        }
        if (status === 0 || status === false) {
            return (
                <span
                    title="Enviar entrega"
                    style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "6px",
                        color: "#DC3545",
                        fontWeight: 500,
                    }}
                >
                    <FaTimes />
                    Denegado
                </span>
            )
        }
        return (
            <span
                title="Enviar entrega"
                style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "6px",
                    color: "#ffffff",
                    backgroundColor: "#f59e0b",
                    padding: "4px 8px",
                    borderRadius: "8px",
                    fontWeight: 500,
                }}
            >
                    <FaClipboardCheck />
                    Pendiente de aprobación
                </span>
        );
    }


    /** Renderiza la celda de FE (cargar/ver/editar factura electrónica). */
    
    /** Renderiza el botón de generación de acta de entrega. */
    const renderGeneratePdfCell = (row) => {
        const hasError = Boolean(row?.reportError);
        const handleClick = () => {
            if (hasError) {
                showCreateDeliveryError(
                    row.reportError,
                    row.reportProductosAlterados,
                    "No se puede generar acta de entrega"
                );
                return;
            }
            handleDeliveryInformationReport(row);
        };

        return (
            <div onClick={handleClick} style={{ cursor: hasError ? "not-allowed" : "pointer" }}>
                <Button
                    variant="outline-primary"
                    title={hasError ? "No disponible: hay productos con error" : "Generar acta de entrega (PDF)"}
                    disabled={hasError}
                    style={hasError ? { pointerEvents: "none" } : undefined}
                >
                    <FaFilePdf />
                </Button>
            </div>
        );
    };

    /** Renderiza botones de evidencia PDF (subir/ver consolidado). */
    const renderEvidenceCell = (row) => {
        return (
            <div>
                <Button
                    variant="outline-secondary"
                    onClick={() => handleUploadFile(row.id, UploadFileEnum.PDF)}
                    disabled={isButtonDisabled(row)}
                    title="Subir consolidado de entrega (PDF)"
                >
                    Subir PDF
                </Button>

                {hasPdfConsolidado(row) && (
                    <Button
                        variant="outline-success"
                        onClick={() => handleViewFile(row.evidencePdf.consolidatedFileUrl)}
                        style={{ marginLeft: 10 }}
                        title="Ver consolidado"
                    >
                        Ver PDF
                    </Button>
                )}
            </div>
        );
    };
const renderFeCell = (row) => {
        const canEdit = canEditRoles.includes(userAuth.rol_id);
        const fePdfUrl = getFePdfUrl(row);

        // 1) Sin consolidado -> no hacer nada
        if (!hasPdfConsolidado(row)) return <span>—</span>;

        // 2) Con consolidado pero SIN FE -> botón para cargar FE (PDF + número)
        if (!hasFeNumber(row)) {
            return (
                <div>
                    <Button
                        variant="outline-info"
                        onClick={() => openFeModal(row.id)}
                        disabled={isButtonDisabled(row) || !canEdit}
                        title="Adjuntar factura electrónica (PDF) y número de FE"
                    >
                        Subir FE
                    </Button>
                </div>
            );
        }

        // 3) Con FE -> mostrar "Ver FE" (si hay PDF FE) + número + botón editar (si procede)
        return (
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span>{row.fe_number}</span>
                {fePdfUrl && (
                    <Button
                        variant="outline-success"
                        onClick={() => handleViewFile(fePdfUrl)}
                        title="Ver Docuemnto cargado"
                    >
                        Ver Documento
                    </Button>
                )}

                {canEdit && (
                    <Button
                        variant="outline-warning"
                        onClick={() => openFeModal(row.id)}
                        disabled={isButtonDisabled(row)}
                        title="Editar Documento"
                    >
                        Editar Documento
                    </Button>
                )}
            </div>
        );
    };

    /** Renderiza botones de acciones (editar, eliminar, aprobar). */
    const renderActionsCell = (row) => {
        const canEdit = canEditRoles.includes(userAuth.rol_id);
        const canDelete = canDeleteRoles.includes(userAuth.rol_id);
        const isSP = userAuth.rol_id === RolesEnum.SUPPLIER;
        const isTechOrAdmin = userAuth.rol_id === RolesEnum.TECHNICAL || isLeaderTechnicalRole(userAuth.rol_id) || userAuth.rol_id === RolesEnum.ADMIN;

        return (
            <div>
                {/* Enviar entrega */}
                {hasPdfOK(row) && isSP && (
                    <Button
                        style={{
                            backgroundColor: "#0d6efd",
                            borderColor: "#0d6efd",
                            color: "#fff",
                            marginRight: 10,
                        }}
                        onClick={() => {
                            setSelectedDeliveryId(row.id);
                            setShowConfirmationModal(true);
                        }}
                        disabled={isButtonDisabled(row)}
                        title="Enviar Entrega"
                    >
                        {renderSendSupplierIcon(row.actions?.aprobadoProveedor)}
                    </Button>
                )}

                {/* aprobación Técnica/Admin (solo si hay consolidado) */}
                {hasPdfOK(row) && isTechOrAdmin && row?.actions?.aprobadoProveedor && (
                    <Button
                        style={{ backgroundColor: "#FFF", marginRight: 10 }}
                        onClick={() => {
                            setSelectedDeliveryId(row.id);
                            setOpenModal(true);
                        }}
                        disabled={isButtonDisabledTecnical(row)}
                        title="aprobación técnica"
                    >
                        {renderApprovalIcon(row.actions?.approvedTechnical)}
                    </Button>
                )}

                {/* Poder editar */}
                {canEdit && (
                    <Button
                        variant="outline-warning"
                        onClick={() => handleEditDelivery(row.id)}
                        style={{ marginRight: 10 }}
                        disabled={isButtonDisabled(row)}
                        title="Editar entrega"
                    >
                        <FaPencilAlt />
                    </Button>
                )}

                {/* Poder eliminar */}
                {canDelete && (
                    <Button
                        variant="outline-danger"
                        onClick={() => handleDeleteDelivery(row.id)}
                        style={{ marginRight: 10 }}
                        disabled={isButtonDisabled(row)}
                        title="Eliminar entrega"
                    >
                        <FaTrash />
                    </Button>
                )}
            </div>
        );
    };

    const renderPhotoCell = (row) => {
        const imagen1Url = getImagen1Url(row);
        const imagen2Url = getImagen2Url(row);

        return (
            <>
                {hasPdfConsolidado(row) && (
                    <div className="d-flex align-items-center gap-2">
                        <Button
                            variant="outline-secondary"
                            onClick={() => handleUploadFile(row.id, UploadFileEnum.EVIDENCE1)}
                            disabled={isButtonDisabled(row)}
                            title="Subir imagen 1"
                        >
                            Subir Imagen 1
                        </Button>

                        {imagen1Url && (
                            <Button
                                variant="outline-success"
                                onClick={() => handleViewFile(imagen1Url)}
                                title="Ver Docuemnto cargado"
                            >
                                Ver Imagen 1
                            </Button>
                        )}

                        <Button
                            variant="outline-secondary"
                            onClick={() => handleUploadFile(row.id, UploadFileEnum.EVIDENCE2)}
                            disabled={isButtonDisabled(row)}
                            title="Subir imagen 2"
                        >
                            Subir Imagen 2
                        </Button>

                        {imagen2Url && (
                            <Button
                                variant="outline-success"
                                onClick={() => handleViewFile(imagen2Url)}
                                title="Ver Docuemnto cargado"
                            >
                                Ver Imagen 2
                            </Button>
                        )}
                    </div>
                )}
            </>
        );
    }

    //===== Columnas =====
    const deliveryColumns = [
        { title: "N° ENTREGA", dataIndex: "id", key: "id", width: 120 },
        { title: "FECHA", dataIndex: "date", key: "date", width: 120 },
        { title: "PROVEEDOR", dataIndex: "supplier", key: "supplier", width: 350 },
        {
            title: "GENERAR ACTA DE ENTREGA",
            dataIndex: "generatePdf",
            key: "generatePdf",
            width: 200,
            render: (_, record) => renderGeneratePdfCell(record),
        },
        {
            title: "CONSOLIDADO DE ENTREGA",
            dataIndex: "evidencePdf",
            key: "evidencePdf",
            width: 250,
            render: (_, record) => renderEvidenceCell(record),
        },
        {
            title: "NÚMERO FE O DOCUMENTO EQUIVALENTE",
            dataIndex: "fe_number",
            key: "fe_number",
            width: 400,
            render: (_, record) => renderFeCell(record),
        },
        {
            title: "EVIDENCIA FOTOGRÁFICA",
            dataIndex: "evidence_photo",
            key: "evidence_photo",
            width: 550,
            render: (_, record) => renderPhotoCell(record),
        },
        { title: "ESTADO", dataIndex: "statusDelivery", key: "statusDelivery", width: 150 },
        {
            title: "OBSERVACIÓN",
            dataIndex: "observation",
            key: "observation",
            width: 280,
            render: (value) => (
                <div
                    style={{
                        whiteSpace: "normal",
                        lineHeight: "1.4",
                        wordWrap: "break-word",
                        overflowWrap: "break-word",
                        textAlign: "justify",
                    }}
                >
                    {value}
                </div>
            ),
        },
        {
            title: "ACCIONES",
            dataIndex: "actions",
            key: "actions",
            width: 350,
            render: (_, record) => renderActionsCell(record),
        },
    ];

    /**
     * Lanza input file oculto para subir consolidado PDF.
     * Actualiza el estado de la fila una vez subido.
     */
    const handleUploadFile = (rowId, fileName) => {
        const input = document.createElement("input");
        input.type = "file";
        input.style.display = "none";

        // Captura el archivo seleccionado. handleFileChange revalida la fila al terminar.
        input.onchange = (e) => {
            const file = e.target.files[0];
            if (file) {
                handleFileChange(file, rowId, fileName);
            }
        };

        document.body.appendChild(input);
        input.click();
        document.body.removeChild(input);
    };

    /**
     * Descarga y abre un PDF en nueva pestaña.
     * @param {string} pdfUrl - URL del archivo PDF.
     */
    const handleViewFile = async (pdfUrl) => {
        if (!pdfUrl) {
            AlertComponent.error('Error', 'No hay un archivo cargado para este producto.');
            return;
        }

        try {
            setLoading(true);
            const res = await filesServices.downloadFile(pdfUrl);

            const { blob, status, type, filename } = res || {};

            if (status === ResponseStatusEnum.OK && blob instanceof Blob) {
                const mime = (type || blob.type || '').toLowerCase();

                // Solo PDF o imágenes
                if (mime.includes('pdf') || mime.startsWith('image/')) {
                    const url = URL.createObjectURL(blob);
                    window.open(url, '_blank', 'noopener,noreferrer');
                    setTimeout(() => URL.revokeObjectURL(url), 60_000);
                }
            }

            if (status === ResponseStatusEnum.NOT_FOUND) {
                AlertComponent.error('Error', 'No se puede descargar el archivo.');
            }
        } catch (error) {
            console.error("Error al descargar archivo:", error);
        } finally {
            setLoading(false);
        }
    };

    //
    const productsToBeDeliveredColumns = [
        { title: "COD", dataIndex: "id", key: "id", width: 100 },
        {
            title: "NOMBRE",
            dataIndex: "name",
            key: "name",
            width: 260,
            render: (value) => (
                <div
                    style={{
                        textAlign: "left",
                        whiteSpace: "normal",
                        overflow: "visible",
                    }}
                >
                    {value}
                </div>
            ),
        },
        {
            title: "DESCRIPCIÓN",
            dataIndex: "description",
            key: "description",
            width: 420,
            render: (value) => (
                <div
                    style={{
                        textAlign: "left",
                        whiteSpace: "normal",
                        wordWrap: "break-word",
                        overflow: "visible",
                    }}
                >
                    {value}
                </div>
            ),
        },
        { title: "CANT. SOLICITADA", dataIndex: "amount", key: "amount", width: 160 },
        {
            title: "CANT. A ENTREGAR",
            dataIndex: "quantityToBeDelivered",
            key: "quantityToBeDelivered",
            width: 180,
            render: (_value, record) => {
                return (
                    <Form.Control
                        type="number"
                        className="small-input form-control-sm"
                        value={record?.quantityToBeDelivered}
                        min="0"
                        onChange={(e) =>
                            handleQuantityChange(record?.id, e.target.value)
                        }
                        style={{
                            width: "100%",
                            padding: "5px",
                            fontSize: "14px",
                            cursor: "pointer",
                        }}
                    />
                );
            },
        },
        {
            title: "ESTADO",
            dataIndex: "state",
            key: "state",
            width: 180,
            render: (_value, record) => {
                return (
                    <select
                        value={record?.state}
                        onChange={(e) =>
                            handleStatusChange(record?.id, parseInt(e.target.value))
                        }
                        style={{
                            width: "100%",
                            padding: "5px",
                            fontSize: "14px",
                            borderRadius: "4px",
                            border: "1px solid #ccc",
                            backgroundColor: "white",
                            cursor: "pointer",
                        }}
                    >
                        {deliveryStatus.map((option) => (
                            <option key={option?.id} value={option?.id}>
                                {option?.label}
                            </option>
                        ))}
                    </select>
                );
            },
        },
    ];

    /** Normaliza productos para mostrarlos en tabla de productos a entregar. */
    const normalizeProductsToBeDeliveredRows = (data) => {
        return data.map((row) => ({
            id: row?.id,
            name: row?.nombre,
            description: row?.descripcion,
            amount: row?.cantidad,
            quantityToBeDelivered: row?.quantityToDeliver || 0,
            state: row?.estado,
        }));
    }

    const buildCreateDeliveryErrorHtml = (errorMessage, productNames) => {
        const safeMessage = escapeHtml(errorMessage || "Hubo un error al intentar crear la entrega.");
        if (!productNames?.length) {
            return `<p style="margin: 0; text-align: left;">${safeMessage}</p>`;
        }

        const items = productNames
            .map((name) => `<li style="margin: 0 0 4px 0;">${escapeHtml(name)}</li>`)
            .join("");

        return `
            <div style="text-align: left;">
                <p style="margin: 0 0 8px 0;">${safeMessage}</p>
                <div style="margin: 0 0 6px 0; font-weight: 600;">Productos:</div>
                <div style="max-height: 86px; overflow: auto; padding-right: 4px;">
                    <ul style="margin: 0 0 0 16px; padding: 0;">${items}</ul>
                </div>
            </div>
        `;
    };

    const showCreateDeliveryError = (errorMessage, productosAlterados = [], title = "Error") => {
        const productNames = Array.isArray(productosAlterados)
            ? productosAlterados
                .map((product) => {
                    if (typeof product === "string") return product;
                    return (
                        product?.nombre ||
                        product?.name ||
                        product?.producto?.nombre ||
                        product?.producto?.name ||
                        product?.producto ||
                        product?.nombre_producto ||
                        product?.producto_nombre ||
                        product?.Producto ||
                        product?.Producto_Nombre ||
                        ""
                    );
                })
                .filter(Boolean)
            : [];

        AlertComponent.Error(title, buildCreateDeliveryErrorHtml(errorMessage, productNames));
    };

    //Crear entrega
    const handleCreateDeliveries = async () => {
        if (!selectedSupplier && userAuth.rol_id === RolesEnum.TERRITORIAL_LINKS) {
            showError('Error', 'Debe escoger al menos una empresa');
            return;
        }

        const dataSupplier = selectedSupplier ? selectedSupplier.value : suppliers[0].id;

        setLoading(true);
        try {
            const { data, status} = await deliveriesServices.productsToBeDelivered(dataSupplier, params.id);

            if (data?.error) {
                showCreateDeliveryError(data?.error, data?.productos_alterados);
                setShowDeliveryForm(false);
                return;
            }

            if (data.length <= 0) {
                showInfo('', 'No tienes productos pendientes');
                setShowDeliveryForm(false);
                return;
            }

            if (status === ResponseStatusEnum.OK  && data.length > 0) {
                const updatedData = data.map(product => ({
                    ...product,
                    estado: 1,
                    quantityToDeliver: product.cantidad
                }));
                setDeliveryProducts(normalizeProductsToBeDeliveredRows(updatedData));
                setShowDeliveryForm(true);
            }

            if (status === ResponseStatusEnum.BAD_REQUEST) {
                showError("Error", "Error al obtener las órdenes de compra");
            }
        } catch (error) {
            console.error("Error obteniendo productos a entregar:", error);
        } finally {
            setLoading(false);
        }
    }

    //
    const handleEditDelivery = async (id) => {
        navigate(`/admin/edit-delivery-order/${id}`)
    }

    //
    const handleDeleteDelivery = async (id) => {
        try {
            const { status} = await deliveriesServices.removeDelivery(id);
            if (status === ResponseStatusEnum.NO_CONTENT) {
                showAlert('Éxito', 'Entrega eliminada exitosamente');
                await revalidateDeliveries();
            }

            if (status === ResponseStatusEnum.METHOD_NOT_ALLOWED) {
                showError('Error', 'NO TIENES PERMISO PARA ESTA ACCIÓN');
            }

            if (status === ResponseStatusEnum.FORBIDDEN) {
                showError('Error', 'NO SE PUEDE ELIMINAR ESTA ENTREGA');
            }
        } catch (error) {
            console.error("Error eliminando la entrega:", error);
        }
    }

    // Helper común para enviar al backend y manejar UX
    const submitApproval = async ({deliveryId, payload, successMessage, handleClose}) => {
        if (loading) return false; // evita doble envío
        if (!deliveryId) {
            showError("Validación", "No se encontró el ID de la entrega.");
            return false;
        }

        try {
            setLoading(true);
            const { status, data } = await deliveriesServices.approveDelivery(deliveryId, payload);

            if (status === ResponseStatusEnum.OK) {
                await refreshRow(deliveryId);
                showAlert("Bien hecho!", successMessage);
                handleClose();
                return true;
            }

            if (status !== ResponseStatusEnum.OK && data?.error) {
                showCreateDeliveryError(data?.error, data?.productos_alterados);
                handleClose();
                return false;
            }

            showError("Error", "No se pudo procesar la solicitud.");
            return false;
        } catch (error) {
            console.error("approveDelivery error:", error);
            showError("Error", error);
            return false;
        } finally {
            setLoading(false);
        }
    };

    //envío del porveedor
    const handleConfirmDelete = async () => {
        // Envío del proveedor (aprobado=1 con observación fija)
        const payload = {
            aprobado: 1,
            observacion: "Enviado por el proveedor",
        };

        await submitApproval({
            deliveryId: selectedDeliveryId,
            payload,
            successMessage: "Entrega enviada exitosamente!",
            handleClose: handleCloseModalConfirm,
        });
    };

    //envío de aprobación del tecnico
    const handleApproveByAudit = async () => {
        // Envío de aprobación/denegación del técnico (requiere acción y comentario)
        const trimmed = (comment || "").trim();

        if (action === 'deny' && trimmed === "") {
            showError("Validación", "Debes escribir un comentario.");
            return;
        }

        const aprobado = action === "approve" ? 1 : 0;
        const payload = { aprobado, observacion: trimmed };

        await submitApproval({
            deliveryId: selectedDeliveryId,
            payload,
            successMessage: `Entrega ${aprobado ? "aprobada" : "denegada"} exitosamente!`,
            handleClose: handleCloseModalApproved
        });
    };

    //Cerrar modal de confirmacion
    const handleCloseModalConfirm = () => {
        setShowConfirmationModal(false);
    };

    //Cerra modal de aprobacion
    const handleCloseModalApproved = () => {
        setOpenModal(false);
        setComment('');
        setAction('approve');
    };

    /**
     * Maneja el envío de archivos al backend (consolidado o FE).
     * @param {File}   file
     * @param {number} deliveryId
     * @param {string} fileName - "pdf" (consolidado) | "fe" (factura electrónica)
     * @param {string} [numeroFE] - Solo se envía cuando fileName === "fe"
     */
    const handleFileChange = async (file, deliveryId, fileName, numeroFE) => {
        if (file) {
            const expectImage = IMAGE_SLOTS.has(fileName);

            const valid = expectImage ? isValidImage(file) : isValidPdf(file);
            if (!valid) {
                showError(
                    'Archivo no válido',
                    expectImage
                        ? 'Solo se permiten imágenes (JPG, PNG).'
                        : 'Solo se permite PDF.'
                );
                return;
            }

            const MAX_SIZE = expectImage ? 5 * 1024 * 1024 : 10 * 1024 * 1024; // 5MB img, 10MB pdf
            if (file.size > MAX_SIZE) {
                showError(
                    'Archivo muy grande',
                    expectImage
                        ? 'La imagen no debe superar 5 MB.'
                        : 'El PDF no debe superar 10 MB.'
                );
                return;
            }

            const formData = new FormData();
            formData.append("ruta", file);
            formData.append("indice", fileName);
            if (fileName === UploadFileEnum.FE && numeroFE) {
                formData.append("numero_fe", numeroFE);
            }

            try {
                const { data, status } = await deliveriesServices.evidenceOfDeliveries(deliveryId, formData);

                if (status === ResponseStatusEnum.CREATED) {
                    showAlert('Éxito', 'Archivo enviado exitosamente');
                    await refreshRow(deliveryId);
                }

                if (status === ResponseStatusEnum.BAD_REQUEST) {
                    console.log(data);
                    if(data?.error) showError('Error', `${data?.error}`); return;
                    showError('Error', 'Error al enviar el archivo'); return;
                }

                if (status === ResponseStatusEnum.INTERNAL_SERVER_ERROR || status !== ResponseStatusEnum.CREATED) {
                    showError('Error', 'Error al enviar el archivo');
                    return;
                }
            } catch (error) {
                console.error("Error al enviar el archivo:", error);
                showError('Error', 'Error al enviar el archivo');
            }
        }
    };

    const handleDeliveryInformationReport = (row) => {
        if (!row?.reportData) {
            showError("Error", "No hay información disponible para generar el acta.");
            return;
        }
        setDeliveryInformation(row.reportData);
        setIsReadyToPrintDeliveryInformation(true);
    }

    const handlePDFPrint = () => {
        const printContent = `
            <html>
                <head>
                  <style>           
                    body {
                      font-family: Arial, sans-serif;
                      margin: 20px;
                      font-size: 10px;
                    }
                    .page-break {
                        page-break-after: always;
                    }
                  </style>
                </head>
                <body>
                  <!-- Inyectamos el HTML del componente -->
                  ${deliveryReportRef.current.innerHTML} 
                </body>
            </html>`;

        printJS({
            printable: printContent,
            type: 'raw-html',
            documentTitle: 'Acta De Entrega',
        });
    }

    /** Maneja cambio de cantidad a entregar en tabla de productos. */
    const handleQuantityChange = (id, value) => {
        const newProducts = deliveryProducts.map((product) => {
            if (product.id === id) {
                return { ...product, quantityToBeDelivered: value.trim() === '' ? '' : Math.max(0, Number(value)) };
            }
            return product;
        });
        setDeliveryProducts(newProducts);
    };

    /** Maneja cambio de estado de producto (entregado, no entregado, parcial). */
    const handleStatusChange = (id, value) => {
        const newProducts = deliveryProducts.map((product) => {
            if (product.id === id) {
                return { ...product, state: value };
            }
            return product;
        });
        setDeliveryProducts(newProducts);
    };

    /** Maneja cambio de estado de producto (entregado, no entregado, parcial). */
    const handleSaveProduct = async () => {
        try {
            setLoading(true);
            //
            const dataSaveProducts = deliveryProducts.map(prod => ({
                producto: prod?.id,
                cantidad: prod?.quantityToBeDelivered,
                estado: prod?.state
            }));

            const dataSupplier = selectedSupplier ? selectedSupplier.value : suppliers[0].id;

            const {data, status} = await deliveriesServices.saveProducts(dataSupplier, params.id, dataSaveProducts);
            if(status === ResponseStatusEnum.OK) {
                showAlert('Éxito', 'Productos entregados correctamente.')
                setShowDeliveryForm(false);
                await revalidateDeliveries();
            }

            if(status === ResponseStatusEnum.BAD_REQUEST) {
                showError('Error', `${data}`);
            }
        } catch (error) {
            showError('Error al guardar los productos', `${error}`);
        } finally {
            setLoading(false);
        }
    };

    /** Modal FE */
    const openFeModal = (deliveryId) => {
        setFeDeliveryId(deliveryId);
        setShowFeModal(true);
    };

    /** Cerrar modal FE */
    const closeFeModal = () => {
        setShowFeModal(false);
        setFeDeliveryId(null);
    };

    //
    const handleSaveFe = async ({ feNumber, feFile }) => {
        try {
            setFeLoading(true);

            if(!feNumber) {
                showError("Error", "Debe agregar el número de Factura ó Documento Equivalente.");
                return;
            }

            // Guardas FE (archivo + número). handleFileChange revalida la fila afectada.
            await handleFileChange(feFile, feDeliveryId, UploadFileEnum.FE, feNumber);
            closeFeModal();
        } catch (error) {
            console.error(error);
            showError("Error", "No se pudo registrar la FE.");
        } finally {
            setFeLoading(false);
        }
    };

    //
    const showAlert = (title, message) => {
        AlertComponent.success(title, message)
    };

    //
    const showError = (title, message) => {
        AlertComponent.error(title, message);
    };

    const showInfo = (title, message) => {
        AlertComponent.info(title, message);
    };

    useEffect(() => {
        if(isReadyToPrintDeliveryInformation) {
         handlePDFPrint();
         setIsReadyToPrintDeliveryInformation(false);
        }
    }, [isReadyToPrintDeliveryInformation]);


    return (
        <>
            <div className="main-container">
                <HeaderImage
                    imageHeader={imgDCSIPeople}
                    titleHeader={'Entrega de productos'}
                    bannerIcon={imgAdd}
                    backgroundIconColor={'#2148C0'}
                    bannerInformation={'Aquí podrás realizar la entrega de todos tus productos.'}
                    backgroundInformationColor={'#F66D1F'}
                />

                {/* Contenedor de la información del usuario */}
                <UserInformation userData={userData} />

                <div className="deliveries-banner">
                    <Container>
                        <Row className="justify-content-end align-items-center">
                            <Col xs={12} md={6} className="deliveries-consolidated">
                                {(userAuth.rol_id === RolesEnum.ADMIN || userAuth.rol_id === RolesEnum.TECHNICAL || isLeaderTechnicalRole(userAuth.rol_id)) && consolidated &&(
                                    <button
                                        onClick={() => handleViewFile(consolidated)}
                                        rel="noopener noreferrer"
                                        className="deliveries-action-button deliveries-action-button--primary"
                                    >
                                        <FaFilePdf /> VER CONSOLIDADO DE COMPRAS
                                    </button>
                                )}
                            </Col>
                        </Row>
                        <Row className="justify-content-end align-items-center mt-4">

                            <Col xs={12} md={8} className="d-flex justify-content-end align-items-center gap-3">
                                {(userAuth.rol_id === RolesEnum.ADMIN || userAuth.rol_id === RolesEnum.TERRITORIAL_LINKS) && (
                                    <Select
                                        value={selectedSupplier}
                                        onChange={(selectedOption) => setSelectedSupplier(selectedOption)}
                                        options={suppliers?.map((opt) => ({ value: opt.id, label: opt.nombre }))}
                                        placeholder="Selecciona una compañía"
                                        classNamePrefix="custom-select"
                                        className="custom-select flex-grow-1"
                                    />
                                )}

                                {(userAuth.rol_id === RolesEnum.ADMIN ||
                                  userAuth.rol_id === RolesEnum.SUPPLIER ||
                                  userAuth.rol_id === RolesEnum.TERRITORIAL_LINKS) && (
                                    <button
                                        onClick={handleCreateDeliveries}
                                        className="deliveries-action-button deliveries-action-button--success d-flex align-items-center"
                                    > <FaPaperPlane /> CREAR ENTREGAS
                                    </button>
                                )}
                            </Col>
                        </Row>
                    </Container>
                </div>

                {(loading || deliveriesLoading) && <Loading text="Cargando..." />}

                <div className="">
                    <Container>
                        <hr/>
                        {listDeliveriesToUser.length > 0 && !showDeliveryForm ? (
                            <>
                                <div className="deliveries-table-card">
                                    <SmartTable
                                        rowKey="id"
                                        columns={deliveryColumns}
                                        dataSource={listDeliveriesToUser}
                                        loading={loading || deliveriesLoading}
                                        total={listDeliveriesToUser.length}
                                        currentPage={deliveriesPage}
                                        defaultPageSize={deliveriesPageSize}
                                        pageSizeOptions={["5", "10", "20"]}
                                        onPageChange={(nextPage, nextPageSize) => {
                                            setDeliveriesPage(nextPage);
                                            setDeliveriesPageSize(nextPageSize);
                                        }}
                                        defaultText="---"
                                        emptyText="No se han realizado entregas."
                                        enableRowSelection={false}
                                        showToolbar={false}
                                        showTableResize={false}
                                        showColumnSettings={false}
                                        scroll={{ x: 2800 }}
                                    />
                                </div>
                                <div className="button-container button-container--deliveries mt-2 d-flex flex-md-row flex-column justify-content-md-end justify-content-center">
                                    <Button
                                        variant="outline-secondary"
                                        onClick={() => navigate(-1)}
                                        className="responsive-button deliveries-back-button deliveries-action-button deliveries-action-button--ghost mb-2 mb-md-0"
                                    >
                                        <FaStepBackward /> ATRÁS
                                    </Button>
                                </div>
                            </>

                        ) : (
                            !showDeliveryForm && (
                                <div className="text-center mt-4">
                                    <p>No se han realizado entregas.</p>
                                </div>
                            )
                        )}

                        {showDeliveryForm && (
                            <>
                                <div className="deliveries-table-card">
                                    <SmartTable
                                        rowKey="id"
                                        columns={productsToBeDeliveredColumns}
                                        dataSource={deliveryProducts}
                                        loading={loading}
                                        total={deliveryProducts.length}
                                        currentPage={productsPage}
                                        defaultPageSize={productsPageSize}
                                        pageSizeOptions={["5", "10", "20"]}
                                        onPageChange={(nextPage, nextPageSize) => {
                                            setProductsPage(nextPage);
                                            setProductsPageSize(nextPageSize);
                                        }}
                                        defaultText="---"
                                        emptyText="No hay productos pendientes para entregar."
                                        enableRowSelection={false}
                                        showToolbar={false}
                                        showTableResize={false}
                                        showColumnSettings={false}
                                        scroll={{ x: 1500 }}
                                    />
                                </div>

                                <div className="button-container button-container--deliveries mt-2 d-flex flex-md-row flex-column justify-content-md-end justify-content-center">
                                    <Button
                                        variant="outline-secondary"
                                        onClick={() => setShowDeliveryForm(false)}
                                        className="responsive-button deliveries-back-button deliveries-action-button deliveries-action-button--ghost mb-2 mb-md-0"
                                    >
                                        <FaStepBackward /> ATRÁS
                                    </Button>

                                    <Button
                                        variant="outline-success"
                                        onClick={handleSaveProduct}
                                        className="responsive-button"
                                        disabled={loading}
                                    >
                                        <FaSave /> GUARDAR ENTREGA
                                    </Button>
                                </div>
                            </>
                    )}
                    </Container>
                </div>

                {/* Aquí renderizas el componente pero lo ocultas */}
                <div style={{display: 'none'}}>
                    {isReadyToPrintDeliveryInformation && (
                        <div ref={deliveryReportRef}>
                            <DeliveryReport deliveryInformation={deliveryInformation} />
                        </div>
                    )}
                </div>

                {/* Modal de FE */}
                <FEModal
                    show={showFeModal}
                    onClose={closeFeModal}
                    loading={feLoading}
                    onSave={handleSaveFe}
                />

                {/* Confirmación Entregas */}
                <ConfirmationModal
                    show={showConfirmationModal}
                    title="Confirmación de envío"
                    message="¿Estás seguro de que deseas enviar esta entrega a revisión?"
                    onConfirm={handleConfirmDelete}
                    onClose={handleCloseModalConfirm}
                />

                {/* Modal de aprobación/denegación */}
                <ApprovedDeniedModal
                    open={openModal}
                    onClose={handleCloseModalApproved}
                    action={action}
                    setAction={setAction}
                    comment={comment}
                    setComment={setComment}
                    onSubmit={handleApproveByAudit}
                />

            </div>
        </>
    )
}
